import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Plus, Trash2 } from "lucide-react";

import Input from "../../components/Input";
import Button from "../../components/Button";
import Loading from "../../components/Loading";
import { api } from "../../api/client";

const numberOrEmpty = (v) =>
  v === "" || v === null || v === undefined ? "" : Number(v);

// Helper function to get photo data URL from base64
const getPhotoDataUrl = (photo, useThumbnail = false) => {
  if (!photo) return null;

  if (photo.contentType) {
    const imageData = useThumbnail && photo.thumbnail ? photo.thumbnail : photo.data;
    if (imageData) {
      const contentType = useThumbnail && photo.thumbnail ? 'image/jpeg' : photo.contentType;
      return `data:${contentType};base64,${imageData}`;
    }
  }

  if (typeof photo === 'string') {
    return photo;
  }

  return null;
};

export default function EventEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [currentPhoto, setCurrentPhoto] = useState(null);

  const loadCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(Array.isArray(res) ? res : res.data || []);
    } catch (e) {
      console.error("Failed to load categories:", e);
      setCategories([]);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/events/${id}`);

      // Load coupons array or initialize empty array
      const coupons = res.coupons && Array.isArray(res.coupons) ? res.coupons : [];

      const mapped = {
        ...res,
        startAt: res.startAt
          ? new Date(res.startAt).toISOString().slice(0, 16)
          : "",
        category: res.category?._id || "",
        coupons: coupons,
      };
      setForm(mapped);

      // Set current photo if exists
      if (res.photos && res.photos.length > 0) {
        setCurrentPhoto(res.photos[0]);
      }
    } catch (e) {
      toast(e.message || "Failed to load event", {
        type: "error",
        autoClose: 2000,
        hideProgressBar: true,
      });
      navigate("/events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast("Please upload a valid image (JPG, PNG, or WebP)", {
        type: "error",
        autoClose: 2000,
        hideProgressBar: true,
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast("Image size should be less than 5MB", {
        type: "error",
        autoClose: 2000,
        hideProgressBar: true,
      });
      return;
    }

    // Store file and create preview
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      setSaving(true);

      // Validate required fields
      if (!form.name?.trim()) {
        throw new Error("Event name is required");
      }
      if (!form.venue?.trim()) {
        throw new Error("Venue is required");
      }
      if (!form.location?.trim()) {
        throw new Error("City is required");
      }
      if (!form.startAt) {
        throw new Error("Start date/time is required");
      }
      if (form.basePrice === "" || form.basePrice === null || form.basePrice === undefined) {
        throw new Error("Base price is required");
      }
      if (form.gstPercent === "" || form.gstPercent === null || form.gstPercent === undefined) {
        throw new Error("GST percent is required");
      }
      if (form.seatsTotal === "" || form.seatsTotal === null || form.seatsTotal === undefined) {
        throw new Error("Total seats is required");
      }
      if (form.seatsAvailable === "" || form.seatsAvailable === null || form.seatsAvailable === undefined) {
        throw new Error("Available seats is required");
      }

      // If there's a new photo, use FormData, otherwise use JSON
      if (photoFile) {
        const formData = new FormData();
        formData.append("name", form.name.trim());
        formData.append("description", form.description || "");
        formData.append("venue", form.venue.trim());
        formData.append("location", form.location.trim());

        if (form.category) {
          formData.append("category", form.category);
        }

        formData.append("startAt", new Date(form.startAt).toISOString());
        formData.append("basePrice", String(form.basePrice));
        formData.append("gstPercent", String(form.gstPercent));
        formData.append("discountedPrice", String(form.discountedPrice || 0));
        formData.append("seatsTotal", String(form.seatsTotal));
        formData.append("seatsAvailable", String(form.seatsAvailable));

        // Add coupons array as JSON string
        if (form.coupons && form.coupons.length > 0) {
          formData.append("coupons", JSON.stringify(form.coupons));
        } else {
          formData.append("coupons", JSON.stringify([]));
        }

        formData.append("photo", photoFile);

        await api.put(`/events/${id}`, formData);
      } else {
        const payload = {
          ...form,
          basePrice: Number(form.basePrice),
          gstPercent: Number(form.gstPercent),
          discountedPrice: Number(form.discountedPrice || 0),
          seatsTotal: Number(form.seatsTotal),
          seatsAvailable: Number(form.seatsAvailable),
          startAt: new Date(form.startAt),
          category: form.category || null,
          coupons: form.coupons || [],
        };

        await api.put(`/events/${id}`, payload);
      }
      toast("Changes saved", {
        type: "success",
        autoClose: 1500,
        hideProgressBar: true,
      });
      navigate("/events");
    } catch (e) {
      toast(e.message || "Save failed", {
        type: "error",
        autoClose: 2000,
        hideProgressBar: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <Loading />;

  return (
    <div className="w-full h-full">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Edit Event</h1>
        <Button
          type="button"
          onClick={() => navigate(-1)}
          text="Back"
          className="bg-gray-600 hover:bg-gray-700"
        />
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Event Name *
            </label>
            <Input
              name="Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          {/* Venue */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Venue *
            </label>
            <Input
              name="Venue *"
              value={form.venue || ""}
              onChange={(e) => setForm({ ...form, venue: e.target.value })}
            />
          </div>

          {/* City */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              City *
            </label>
            <Input
              name="City *"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Category
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={form.category || ""}
              onChange={(e) =>
                setForm({ ...form, category: e.target.value })
              }
            >
              <option value="">-- Select Category --</option>
              {categories
                .filter((cat) => cat.isActive)
                .map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
            </select>
          </div>

          {/* Start */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Start Date &amp; Time *
            </label>
            <Input
              name="Start At *"
              type="datetime-local"
              value={form.startAt}
              onChange={(e) =>
                setForm({ ...form, startAt: e.target.value })
              }
            />
          </div>

          {/* Base Price */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Base Price (₹) *
            </label>
            <Input
              name="Base Price (₹) *"
              type="number"
              value={form.basePrice}
              onChange={(e) =>
                setForm({
                  ...form,
                  basePrice: numberOrEmpty(e.target.value),
                })
              }
            />
          </div>

          {/* GST */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              GST %
            </label>
            <Input
              name="GST % *"
              type="number"
              value={form.gstPercent}
              onChange={(e) =>
                setForm({
                  ...form,
                  gstPercent: numberOrEmpty(e.target.value),
                })
              }
            />
          </div>

          {/* Discount */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Discount Amount (₹)
            </label>
            <Input
              name="Discount Amount (₹)"
              type="number"
              value={form.discountedPrice}
              onChange={(e) =>
                setForm({
                  ...form,
                  discountedPrice: numberOrEmpty(e.target.value),
                })
              }
            />
          </div>

          {/* Coupons Section */}
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-2">
              Coupons
            </label>
            <div className="space-y-2">
              {form.coupons && form.coupons.map((coupon, index) => (
                <div key={index} className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Input
                    width="w-32"
                    placeholder="CODE"
                    value={coupon.code}
                    onChange={(e) => {
                      const newCoupons = [...form.coupons];
                      newCoupons[index].code = e.target.value.toUpperCase();
                      setForm({ ...form, coupons: newCoupons });
                    }}
                  />
                  <select
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    value={coupon.type}
                    onChange={(e) => {
                      const newCoupons = [...form.coupons];
                      newCoupons[index].type = e.target.value;
                      setForm({ ...form, coupons: newCoupons });
                    }}
                  >
                    <option value="percent">Percent (%)</option>
                    <option value="flat">Flat (₹)</option>
                  </select>
                  <Input
                    width="w-24"
                    type="number"
                    placeholder="Amount"
                    value={coupon.amount}
                    onChange={(e) => {
                      const newCoupons = [...form.coupons];
                      newCoupons[index].amount = numberOrEmpty(e.target.value);
                      setForm({ ...form, coupons: newCoupons });
                    }}
                  />
                  <label className="flex items-center gap-1 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={coupon.isActive}
                      onChange={(e) => {
                        const newCoupons = [...form.coupons];
                        newCoupons[index].isActive = e.target.checked;
                        setForm({ ...form, coupons: newCoupons });
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-gray-700">Active</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const newCoupons = form.coupons.filter((_, i) => i !== index);
                      setForm({ ...form, coupons: newCoupons });
                    }}
                    className="ml-auto p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setForm({
                    ...form,
                    coupons: [
                      ...(form.coupons || []),
                      { code: "", type: "percent", amount: "", isActive: true }
                    ]
                  });
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md border border-blue-200"
              >
                <Plus size={16} />
                Add Coupon
              </button>
            </div>
          </div>

          {/* Seats Total */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Total Seats *
            </label>
            <Input
              name="Total Seats *"
              type="number"
              value={form.seatsTotal}
              onChange={(e) =>
                setForm({
                  ...form,
                  seatsTotal: numberOrEmpty(e.target.value),
                })
              }
            />
          </div>

          {/* Seats Available */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Available Seats *
            </label>
            <Input
              name="Available Seats *"
              type="number"
              value={form.seatsAvailable}
              onChange={(e) =>
                setForm({
                  ...form,
                  seatsAvailable: numberOrEmpty(e.target.value),
                })
              }
            />
          </div>
        </div>

        {/* Description */}
        <div className="mt-4">
          <label className="block text-xs font-semibold text-gray-500 mb-1">
            Description
          </label>
          <Input
            width="w-full"
            name="Description"
            value={form.description || ""}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
          />
        </div>

        {/* Photo Upload Section */}
        <div className="mt-4">
          <label className="block text-xs font-semibold text-gray-500 mb-1">
            Event Photo
          </label>

          {/* Current Photo Display */}
          {currentPhoto && !photoPreview && (
            <div className="mb-3">
              <p className="text-xs text-gray-600 mb-2">Current Photo:</p>
              <img
                src={getPhotoDataUrl(currentPhoto, false)}
                alt="Current event"
                className="w-48 h-48 object-cover rounded border border-gray-300"
              />
            </div>
          )}

          {/* New Photo Preview */}
          {photoPreview && (
            <div className="mb-3">
              <p className="text-xs text-gray-600 mb-2">New Photo Preview:</p>
              <img
                src={photoPreview}
                alt="New event preview"
                className="w-48 h-48 object-cover rounded border border-gray-300"
              />
              <button
                type="button"
                onClick={() => {
                  setPhotoFile(null);
                  setPhotoPreview("");
                }}
                className="mt-2 text-xs text-red-500 hover:text-red-700"
              >
                Remove new photo
              </button>
            </div>
          )}

          {/* File Input */}
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handlePhotoUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
          />
          <p className="text-xs text-gray-500 mt-1">
            {currentPhoto ? "Upload a new photo to replace the current one" : "Upload a photo for this event"}
          </p>
        </div>

        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className={`${
              saving ? "bg-gray-300" : "bg-emerald-500 hover:bg-emerald-600"
            } text-white font-bold uppercase text-sm px-6 py-2 rounded shadow`}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
